import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../../styles/AddShow.css';
import ProtectedRoute from '../../components/ProtectedRoute';
import {
  buildChangeDescription,
  formatAdminBoolean,
  formatAdminDateTime,
  recordAdminUpdate
} from '../../utils/adminUpdates';

const SHOW_IMAGE_PRESETS = {
  Raw: {
    sfondo: 'https://www.wwe.com/f/styles/shows_hub_premier_hero_med/public/all/2025/02/20250102_RAW_Netflix_ShowHub--7b447bacd3984e5269da2eda2f4d247c.jpg',
    logo: 'https://www.wwe.com/f/styles/wwe_show_logo_l/public/all/2024/12/RAW_Primary_Logo_For_Any_Background_(1)--de3612b9543981440a24489e0e568d9b.png',
    isExtra: false
  },
  SmackDown: {
    sfondo: 'https://www.wwe.com/f/styles/shows_hub_premier_hero_med/public/all/2024/09/20240913_SD_ShowHub_clean--134655bf5cc180059b4af519a5e1e708.jpg',
    logo: 'https://www.wwe.com/f/styles/wwe_show_logo_l/public/all/2024/09/Smackdown_LOGO--c8e0b09904cb1356d309a8ea2acb0422.png',
    isExtra: false
  },
  NXT: {
    sfondo: 'https://www.wwe.com/f/styles/gallery_img_l/public/all/2024/10/20240930_NXTonCW_ShowHub--cac7cda26a1367a5672f0fbca5f84550.jpg',
    logo: 'https://www.wwe.com/f/styles/wwe_show_logo_l/public/all/2024/10/NXTonCW--0f06792893241464479338f55596a5bf.png',
    isExtra: true
  },
  AAA: {
    sfondo: 'https://firebasestorage.googleapis.com/v0/b/fantawwe-83b60.firebasestorage.app/o/wrestlers%2Faaa-sfondo.jpg?alt=media&token=c79ee7ae-2561-4a88-a769-f13ee78bf740',
    logo: 'https://www.wwe.com/f/styles/wwe_show_logo_l/public/2025/08/AAA_Channel_Logo_Color.png',
    isExtra: true
  },
  "TNA iMPACT": {
    sfondo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSbZLZDh7cuE6sa83bqhnaUPrHsVHzQC5Qcrg&s',
    logo: 'https://tnawrestling.com/assets/img/logo.webp',
    isExtra: true
  }
};

const isWeeklyShowType = (showType) => showType === 'weekly' || showType === 'weekly-special';

const normalizeImageUrl = (value) => (value || '').trim();

const isSameCalendarDate = (firstDate, secondDate) => (
  firstDate.getFullYear() === secondDate.getFullYear() &&
  firstDate.getMonth() === secondDate.getMonth() &&
  firstDate.getDate() === secondDate.getDate()
);

const getImageUrlError = (value) => {
  const imageUrl = normalizeImageUrl(value);

  if (!imageUrl) {
    return 'Inserisci un URL immagine.';
  }

  if (/\s/.test(imageUrl)) {
    return "Rimuovi spazi o ritorni a capo dall'URL.";
  }

  try {
    const isRootRelative = imageUrl.startsWith('/');
    const isAbsoluteHttp = /^https?:\/\//i.test(imageUrl);
    const parsedUrl = new URL(imageUrl, 'https://fantawrestling.local');

    if (!isRootRelative && !isAbsoluteHttp) {
      return 'Usa un URL completo http/https o un percorso che inizia con /.';
    }

    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return 'Sono supportati solo URL http/https.';
    }
  } catch (error) {
    return 'URL immagine non valido.';
  }

  return '';
};

function ImagePreview({ field, label, src, variant, onStatusChange }) {
  const [status, setStatus] = useState('idle');
  const normalizedSrc = normalizeImageUrl(src);
  const validationError = normalizedSrc ? getImageUrlError(normalizedSrc) : '';
  const isLogo = variant === 'logo';

  useEffect(() => {
    if (!normalizedSrc) {
      setStatus('idle');
      onStatusChange(field, 'idle', '', '');
      return;
    }

    if (validationError) {
      setStatus('invalid');
      onStatusChange(field, 'invalid', validationError, normalizedSrc);
      return;
    }

    setStatus('loading');
    onStatusChange(field, 'loading', '', normalizedSrc);
  }, [field, normalizedSrc, onStatusChange, validationError]);

  if (!normalizedSrc) {
    return null;
  }

  const wrapperClassName = isLogo
    ? 'bg-light rounded p-2 d-flex align-items-center justify-content-center position-relative'
    : 'ratio ratio-16x9 bg-light rounded overflow-hidden position-relative';

  const imageClassName = isLogo
    ? 'img-fluid h-100 object-fit-contain'
    : 'object-fit-cover';

  const message = status === 'error'
    ? `${label} non caricato. Verifica che l'URL punti direttamente a un'immagine pubblica.`
    : validationError;

  return (
    <div className="col-sm-6">
      <div className={wrapperClassName} style={isLogo ? { height: '100px' } : undefined}>
        {message ? (
          <div className="p-3 text-danger small text-center">
            <i className="bi bi-exclamation-triangle me-1"></i>
            {message}
          </div>
        ) : (
          <>
            {status === 'loading' && (
              <div className="position-absolute top-50 start-50 translate-middle small text-muted">
                Caricamento...
              </div>
            )}
            <img
              key={normalizedSrc}
              src={normalizedSrc}
              alt={`${label} preview`}
              className={imageClassName}
              onLoad={(event) => {
                event.currentTarget.style.display = '';
                setStatus('loaded');
                onStatusChange(field, 'loaded', '', normalizedSrc);
              }}
              onError={() => {
                setStatus('error');
                onStatusChange(
                  field,
                  'error',
                  `${label} non caricato. Verifica che l'URL punti direttamente a un'immagine pubblica.`,
                  normalizedSrc
                );
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function AddShow() {
  const createEmptyImagePreviewState = () => ({
    sfondo: { src: '', status: 'idle', message: '' },
    logo: { src: '', status: 'idle', message: '' }
  });

  const mapShowDateTime = (timestamp) => {
    const dateValue = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const yyyy = dateValue.getFullYear();
    const mm = String(dateValue.getMonth() + 1).padStart(2, '0');
    const dd = String(dateValue.getDate()).padStart(2, '0');
    const hh = String(dateValue.getHours()).padStart(2, '0');
    const min = String(dateValue.getMinutes()).padStart(2, '0');

    return {
      data: `${yyyy}-${mm}-${dd}`,
      ora: `${hh}:${min}`,
      dateValue
    };
  };

  // Stati per il form
  const [nome, setNome] = useState('');
  const [data, setData] = useState('');
  const [ora, setOra] = useState('');
  const [tipo, setTipo] = useState('weekly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shows, setShows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [duplicatingShow, setDuplicatingShow] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pastPage, setPastPage] = useState(1);
  const navigate = useNavigate();
  const [customNome, setCustomNome] = useState('');
  const [sfondo, setSfondo] = useState('');
  const [logo, setLogo] = useState('');
  const [isExtra, setIsExtra] = useState(false);
  const [imagePreviewState, setImagePreviewState] = useState(createEmptyImagePreviewState);
  const pastShowsPerPage = 9;
  const isDuplicateMode = Boolean(duplicatingShow);
  const formModeLabel = editingId ? 'Modifica Show' : isDuplicateMode ? 'Duplica Show' : 'Crea Nuovo Show';

  // Fetch di tutti gli show da Firestore
  const fetchShows = useCallback(async () => {
    try {
      const q = query(collection(db, 'shows'), orderBy('data', 'desc'));
      const querySnapshot = await getDocs(q);
      const showsData = await Promise.all(
        querySnapshot.docs.map(async (showDoc) => {
          const showSnapshotData = showDoc.data();
          const { data: dateOnly, ora: time, dateValue } = mapShowDateTime(showSnapshotData.data);
          const [liveParticipationsSnap, draftParticipationsSnap] = await Promise.all([
            getDocs(collection(db, 'shows', showDoc.id, 'participations')),
            getDocs(collection(db, 'shows', showDoc.id, 'draft'))
          ]);

          return {
            id: showDoc.id,
            ...showSnapshotData,
            data: dateOnly,
            ora: time,
            dateValue,
            isExtra: showSnapshotData.isExtra || false,
            liveCount: liveParticipationsSnap.size,
            draftCount: draftParticipationsSnap.size
          };
        })
      );
      // Manteniamo l'elenco base dal più recente al meno recente.
      const sorted = showsData.sort((a, b) => b.dateValue - a.dateValue);
      setShows(sorted);
      setPastPage(1);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Carica tutti gli show (ordinati per data discendente)
  useEffect(() => {
    fetchShows();
  }, [fetchShows]);

  const handleImagePreviewStatus = useCallback((field, status, message, src) => {
    setImagePreviewState(prev => {
      const current = prev[field];

      if (current.src === src && current.status === status && current.message === message) {
        return prev;
      }

      return {
        ...prev,
        [field]: { src, status, message }
      };
    });
  }, []);

  const applyShowPreset = (showName) => {
    const preset = SHOW_IMAGE_PRESETS[showName];

    if (!preset) return;

    setSfondo(preset.sfondo);
    setLogo(preset.logo);
    setIsExtra(preset.isExtra);
  };

  const handleShowNameChange = (value) => {
    setNome(value);

    if (value === 'Altro' || value === '') {
      setCustomNome('');
      setSfondo('');
      setLogo('');
      return;
    }

    applyShowPreset(value);
  };

  const handleShowTypeChange = (value) => {
    const resolvedName = getResolvedShowName();

    setTipo(value);

    if (isWeeklyShowType(value)) {
      if (SHOW_IMAGE_PRESETS[resolvedName]) {
        setNome(resolvedName);
        setCustomNome('');
        applyShowPreset(resolvedName);
      } else if (resolvedName) {
        setNome('Altro');
        setCustomNome(resolvedName);
      } else {
        setNome('');
        setCustomNome('');
      }
      return;
    }

    if (nome === 'Altro') {
      setNome(resolvedName);
      setCustomNome('');
    }
  };

  const getResolvedShowName = () => {
    if (isWeeklyShowType(tipo) && nome === 'Altro') {
      return customNome.trim();
    }

    return nome.trim();
  };

  const getDuplicatedShowPayload = (baseShow, nextShowData) => {
    if (!baseShow) return nextShowData;

    const duplicatedBaseData = Object.entries(baseShow).reduce((acc, [key, value]) => {
      if (['id', 'data', 'ora', 'dateValue', 'liveCount', 'draftCount'].includes(key)) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});

    return {
      ...duplicatedBaseData,
      ...nextShowData,
      autoPublishDraftsTargetAt: null,
      autoPublishDraftsHandledForDate: null,
      autoPublishDraftsHandledAt: null
    };
  };

  // Gestione submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const normalizedSfondo = normalizeImageUrl(sfondo);
      const normalizedLogo = normalizeImageUrl(logo);
      const sfondoUrlError = getImageUrlError(normalizedSfondo);
      const logoUrlError = getImageUrlError(normalizedLogo);

      if (sfondoUrlError || logoUrlError) {
        setImagePreviewState(prev => ({
          ...prev,
          sfondo: { src: normalizedSfondo, status: sfondoUrlError ? 'invalid' : prev.sfondo.status, message: sfondoUrlError },
          logo: { src: normalizedLogo, status: logoUrlError ? 'invalid' : prev.logo.status, message: logoUrlError }
        }));
        alert('Correggi gli URL delle immagini prima di salvare.');
        return;
      }

      const failedPreview = [
        ['sfondo', normalizedSfondo],
        ['logo', normalizedLogo]
      ].find(([field, imageUrl]) => (
        imagePreviewState[field]?.src === imageUrl &&
        imagePreviewState[field]?.status === 'error'
      ));

      if (failedPreview) {
        alert(imagePreviewState[failedPreview[0]].message || 'Una preview immagine non e stata caricata.');
        return;
      }

      if (duplicatingShow && data === duplicatingShow.data) {
        alert('Per duplicare lo show devi scegliere una data diversa da quella originale.');
        return;
      }

      const dataOraString = ora ? `${data}T${ora}:00` : `${data}T00:00:00`;
      const nextShowData = {
        nome: getResolvedShowName(),
        data: Timestamp.fromDate(new Date(dataOraString)),
        type: tipo,
        sfondo: normalizedSfondo,
        logo: normalizedLogo,
        isExtra,
        createdAt: Timestamp.now()
      };
      const showData = getDuplicatedShowPayload(duplicatingShow, nextShowData);

      if (editingId) {
        const previousShow = shows.find(item => item.id === editingId);
        await updateDoc(doc(db, 'shows', editingId), showData);
        await recordAdminUpdate({
          type: 'Show',
          action: 'Modifica',
          title: showData.nome,
          description: buildChangeDescription('Show modificato', previousShow, showData, [
            { key: 'nome', label: 'nome' },
            { key: 'data', label: 'data e ora', getBefore: show => show?.dateValue, format: formatAdminDateTime },
            { key: 'type', label: 'tipo' },
            { key: 'sfondo', label: 'sfondo', renderChange: () => 'sfondo aggiornato' },
            { key: 'logo', label: 'logo', renderChange: () => 'logo aggiornato' },
            { key: 'isExtra', label: 'extra', format: formatAdminBoolean }
          ]),
          entityType: 'shows',
          entityId: editingId
        });
      } else {
        const showRef = await addDoc(collection(db, 'shows'), showData);
        await recordAdminUpdate({
          type: 'Show',
          action: duplicatingShow ? 'Duplicazione' : 'Creazione',
          title: showData.nome,
          description: duplicatingShow
            ? `Show duplicato da ${duplicatingShow.nome}: nuova data ${data}${ora ? ` ${ora}` : ''}`
            : `Nuovo show creato: ${showData.nome}`,
          entityType: 'shows',
          entityId: showRef.id
        });
      }

      await fetchShows();
      resetForm();
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      alert('Errore durante il salvataggio. Controlla la console per i dettagli.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modifica show
  const handleEdit = (show) => {
    const showType = show.type || 'weekly';
    const isCustomWeeklyShow = isWeeklyShowType(showType) && !SHOW_IMAGE_PRESETS[show.nome];

    setEditingId(show.id);
    setDuplicatingShow(null);
    setShowCreateForm(true);
    setNome(isCustomWeeklyShow ? 'Altro' : (show.nome || ''));
    setCustomNome(isCustomWeeklyShow ? (show.nome || '') : '');
    setData(show.data);
    setOra(show.ora || '');
    setTipo(showType);
    setSfondo(show.sfondo || '');
    setLogo(show.logo || '');
    setIsExtra(show.isExtra || false);
    setImagePreviewState(createEmptyImagePreviewState());
  };

  const handleDuplicate = (show) => {
    const showType = show.type || 'weekly';
    const isCustomWeeklyShow = isWeeklyShowType(showType) && !SHOW_IMAGE_PRESETS[show.nome];

    setEditingId(null);
    setDuplicatingShow(show);
    setShowCreateForm(true);
    setNome(isCustomWeeklyShow ? 'Altro' : (show.nome || ''));
    setCustomNome(isCustomWeeklyShow ? (show.nome || '') : '');
    setData('');
    setOra(show.ora || '');
    setTipo(showType);
    setSfondo(show.sfondo || '');
    setLogo(show.logo || '');
    setIsExtra(show.isExtra || false);
    setImagePreviewState(createEmptyImagePreviewState());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Elimina show
  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo show?')) {
      try {
        const show = shows.find(item => item.id === id);
        await deleteDoc(doc(db, 'shows', id));
        await recordAdminUpdate({
          type: 'Show',
          action: 'Eliminazione',
          title: show?.nome || id,
          description: `Show eliminato: ${show?.nome || id}`,
          entityType: 'shows',
          entityId: id
        });
        await fetchShows();
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Toggle stato Extra
  const handleToggleExtra = async (id, newValue) => {
    try {
      const show = shows.find(item => item.id === id);
      await updateDoc(doc(db, 'shows', id), { isExtra: newValue });
      await recordAdminUpdate({
        type: 'Show',
        action: 'Modifica',
        title: show?.nome || id,
        description: `${show?.nome || 'Show'} impostato come ${newValue ? 'Extra' : 'Main roster'}`,
        entityType: 'shows',
        entityId: id
      });
      await fetchShows();
    } catch (error) {
      console.error('Errore durante l\'aggiornamento:', error);
      alert('Errore durante l\'aggiornamento');
    }
  };

  // Reset form
  const resetForm = () => {
    setEditingId(null);
    setDuplicatingShow(null);
    setShowCreateForm(false);
    setNome('');
    setCustomNome('');
    setData('');
    setOra('');
    setTipo('weekly');
    setSfondo('');
    setLogo('');
    setIsExtra(false);
    setImagePreviewState(createEmptyImagePreviewState());
  };

  // Separa show futuri (oggi o nel futuro) e passati
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const futureShows = shows
    .filter(show => show.dateValue >= today)
    .sort((a, b) => a.dateValue - b.dateValue);
  const pastShows = shows
    .filter(show => show.dateValue < today)
    .sort((a, b) => b.dateValue - a.dateValue);
  const yesterdayShows = pastShows.filter(show => isSameCalendarDate(show.dateValue, yesterday));
  const olderPastShows = pastShows.filter(show => !isSameCalendarDate(show.dateValue, yesterday));
  const totalPastPages = Math.max(1, Math.ceil(olderPastShows.length / pastShowsPerPage));
  const paginatedPastShows = olderPastShows.slice(
    (pastPage - 1) * pastShowsPerPage,
    pastPage * pastShowsPerPage
  );

  // Ottiene il badge per il tipo di show
  const getTypeBadge = (type) => {
    const badges = {
      'weekly': { class: 'bg-primary', text: 'Settimanale' },
      'weekly-special': { class: 'bg-info', text: 'Speciale TV' },
      'ple': { class: 'bg-brand-secondary text-brand-primary', text: 'PLE' },
      'ple-free': { class: 'bg-purple text-white', text: 'Special' }
    };
    const badge = badges[type] || { class: 'bg-secondary', text: type };
    return <span className={`badge ${badge.class} px-2 py-1`}>{badge.text}</span>;
  };

  // Restituisce la data del prossimo giorno della settimana (0=domenica, 1=lunedì, ..., 6=sabato)
  const formatCardDateBits = (dateValue) => {
    const weekday = dateValue
      .toLocaleDateString('it-IT', { weekday: 'short' })
      .replace('.', '')
      .slice(0, 3);
    const day = dateValue.toLocaleDateString('it-IT', { day: '2-digit' });
    const month = dateValue
      .toLocaleDateString('it-IT', { month: 'short' })
      .replace('.', '');

    return {
      weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
      day,
      month: month.charAt(0).toUpperCase() + month.slice(1)
    };
  };

  const renderShowCard = (show, options = {}) => {
    const dateBits = formatCardDateBits(show.dateValue);
    const cardIdSuffix = options.cardIdSuffix || show.id;

    return (
      <div key={show.id} className={options.columnClassName || 'col-12 col-md-6 col-lg-4'}>
        <div className={`card h-100 shadow-sm add-show-card-shell ${show.isExtra ? 'border-warning' : ''} ${options.cardClassName || ''}`}>
          <div className="add-show-card-shell__media">
            {show.sfondo ? (
              <img
                src={show.sfondo}
                className="card-img-top add-show-card-shell__cover"
                alt={show.nome}
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <div className="card-img-top add-show-card-shell__cover d-flex align-items-center justify-content-center bg-light">
                <i className="bi bi-image text-secondary" style={{ fontSize: '2rem' }}></i>
              </div>
            )}
            {show.logo && (
              <img
                src={show.logo}
                alt="logo"
                className="add-show-card-shell__logo"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div className="add-show-card-shell__type">
              {getTypeBadge(show.type)}
            </div>
          </div>

          <div className="card-body add-show-card-shell__body">
            <div className="add-show-card-shell__header">
              <h5 className="card-title mb-0">{show.nome}</h5>
              <label
                className={`add-show-card-shell__toggle ${show.isExtra ? 'is-active' : ''}`}
                htmlFor={`extra-toggle-${cardIdSuffix}`}
              >
                <input
                  className="add-show-card-shell__toggle-input"
                  type="checkbox"
                  id={`extra-toggle-${cardIdSuffix}`}
                  checked={show.isExtra}
                  onChange={(e) => handleToggleExtra(show.id, e.target.checked)}
                />
                <span className="add-show-card-shell__toggle-text">
                  {show.isExtra ? 'Extra' : 'MR'}
                </span>
              </label>
            </div>

            <div className="add-show-card-shell__info-grid">
              <div className="add-show-card-shell__info add-show-card-shell__info--date">
                <span className="add-show-card-shell__date-weekday">{dateBits.weekday}</span>
                <strong className="add-show-card-shell__date-day">{dateBits.day}</strong>
                <span className="add-show-card-shell__date-month">{dateBits.month}</span>
              </div>
              <div className="add-show-card-shell__info border-brand-primary-ultralight">
                <span className="add-show-card-shell__info-label">Ora</span>
                <strong className="add-show-card-shell__info-value add-show-card-shell__info-value--time">
                  {show.ora || '--:--'}
                </strong>
              </div>
              <div className="add-show-card-shell__info border-info">
                <span className="add-show-card-shell__info-label">Live</span>
                <strong className="add-show-card-shell__info-value">{show.liveCount || 0}</strong>
              </div>
              <div className="add-show-card-shell__info border-warning">
                <span className="add-show-card-shell__info-label">Draft</span>
                <strong className="add-show-card-shell__info-value">{show.draftCount || 0}</strong>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-2 add-show-card-shell__actions">
              <button className="btn btn-sm btn-outline-primary flex-fill" onClick={() => handleEdit(show)}>
                <i className="bi bi-pencil"></i> Modifica
              </button>
              <button className="btn btn-sm btn-outline-success flex-fill" onClick={() => navigate(`/shows/${show.id}/edit-show`)}>
                <i className="bi bi-people-fill"></i> Partecipazioni
              </button>
              <div className="btn-group btn-group-sm" role="group" aria-label={`Azioni rapide ${show.nome}`}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handleDuplicate(show)}
                  title="Duplica lo show cambiando solo la data"
                >
                  <i className="bi bi-copy"></i>
                </button>
                <button className="btn btn-outline-danger" onClick={() => handleDelete(show.id)} title="Elimina">
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getNextWeekdayDate = (targetWeekday, hour = 0, minute = 0) => {
    const today = new Date();
    const currentWeekday = today.getDay(); // 0=dom, 1=lun, ..., 6=sab
    let daysToAdd = targetWeekday - currentWeekday;
    if (daysToAdd <= 0) daysToAdd += 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysToAdd);
    nextDate.setHours(hour, minute, 0, 0);
    return nextDate;
  };

  // Quick create: imposta il form per lo show settimanale
  const quickCreateShow = (showName, weekday, hour, minute) => {
    // Resetta eventuale modifica in corso
    setEditingId(null);
    setDuplicatingShow(null);
    // Apre il form
    setShowCreateForm(true);
    // Imposta i campi
    setNome(showName);
    setTipo('weekly');
    setCustomNome('');
    applyShowPreset(showName);
    // Calcola prossima data utile
    const nextDate = getNextWeekdayDate(weekday, hour, minute);
    const yyyy = nextDate.getFullYear();
    const mm = String(nextDate.getMonth() + 1).padStart(2, '0');
    const dd = String(nextDate.getDate()).padStart(2, '0');
    setData(`${yyyy}-${mm}-${dd}`);
    const hh = String(nextDate.getHours()).padStart(2, '0');
    const min = String(nextDate.getMinutes()).padStart(2, '0');
    setOra(`${hh}:${min}`);
    // Sfondo e logo vengono aggiornati dal preset scelto.
  };

  return (
    <ProtectedRoute>
      <div className="container admin-page py-3 py-lg-4">

        {/* Card Form */}
        <div className="card shadow-lg mb-4">
          <div className="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h2 className="h5 mb-0 d-flex align-items-center">
              <i className={`bi ${editingId ? 'bi-pencil' : isDuplicateMode ? 'bi-copy' : 'bi-plus-circle'} me-2`}></i>
              {formModeLabel}
            </h2>
            {!editingId && !isDuplicateMode && (
              <button
                type="button"
                className="btn btn-outline-light btn-sm"
                onClick={() => setShowCreateForm(v => !v)}
              >
                <i className={`bi me-2 ${showCreateForm ? 'bi-dash-circle' : 'bi-plus-circle'}`}></i>
                {showCreateForm ? 'Chiudi' : 'Apri'}
              </button>
            )}
          </div>

          {(editingId || showCreateForm) && (
            <div className="card-body p-3 p-md-4">
              <form onSubmit={handleSubmit}>
                {isDuplicateMode && (
                  <div className="alert alert-info mb-3">
                    <i className="bi bi-copy me-2"></i>
                    Stai duplicando <strong>{duplicatingShow?.nome}</strong>. Cambia solo la nuova data: il resto restera identico.
                  </div>
                )}
                <div className="row g-3">
                  {/* Data e Ora */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">{isDuplicateMode ? 'Nuova Data*' : 'Data*'}</label>
                    <div className="d-flex flex-column flex-sm-row gap-2">
                      <input
                        type="date"
                        className="form-control"
                        value={data}
                        onChange={(e) => setData(e.target.value)}
                        required
                      />
                      <input
                        type="time"
                        className="form-control"
                        value={ora}
                        onChange={(e) => setOra(e.target.value)}
                        placeholder="HH:mm"
                        disabled={isDuplicateMode}
                      />
                    </div>
                    <small className="text-muted">
                      {isDuplicateMode
                        ? `Data originale: ${duplicatingShow?.data}${duplicatingShow?.ora ? ` alle ${duplicatingShow.ora}` : ''}. L'ora rimane identica.`
                        : "L'ora è opzionale (default 00:00)"}
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Tipo*</label>
                    <select
                      className="form-select"
                      value={tipo}
                      onChange={(e) => handleShowTypeChange(e.target.value)}
                      disabled={isDuplicateMode}
                    >
                      <option value="weekly">Settimanale</option>
                      <option value="ple">PLE (Evento Principale)</option>
                      <option value="weekly-special">Speciale Televisivo</option>
                      <option value="ple-free">Finto PLE (SNME)</option>
                    </select>
                  </div>

                  {/* Nome Show */}
                  <div className="col-12">
                    <label className="form-label fw-bold">Nome Show*</label>
                    {(tipo === 'weekly' || tipo === 'weekly-special') ? (
                      <div className="d-flex flex-column flex-sm-row gap-2">
                        <select
                          className="form-select"
                          value={nome}
                          onChange={(e) => handleShowNameChange(e.target.value)}
                          disabled={isDuplicateMode}
                          required
                        >
                          <option value="">-- Seleziona --</option>
                          <option value="Raw">Raw</option>
                          <option value="SmackDown">SmackDown</option>
                          <option value="NXT">NXT</option>
                          <option value="AAA">AAA</option>
                          <option value="TNA iMPACT">TNA iMPACT</option>
                          <option value="Altro">Altro</option>
                        </select>
                        {nome === 'Altro' && (
                          <input
                            type="text"
                            className="form-control"
                            value={customNome}
                            onChange={(e) => setCustomNome(e.target.value)}
                            placeholder="Nome personalizzato"
                            disabled={isDuplicateMode}
                            required
                          />
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        placeholder="Es: WrestleMania"
                        disabled={isDuplicateMode}
                        required
                      />
                    )}
                  </div>

                  {/* Extra Switch */}
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="extraSwitch"
                        checked={isExtra}
                        onChange={(e) => setIsExtra(e.target.checked)}
                        disabled={isDuplicateMode}
                      />
                      <label className="form-check-label fw-bold" htmlFor="extraSwitch">
                        Extra
                      </label>
                    </div>
                  </div>

                  {/* URL Immagini */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">URL Sfondo*</label>
                    <input
                      type="text"
                      inputMode="url"
                      className={`form-control ${sfondo && getImageUrlError(sfondo) ? 'is-invalid' : ''}`}
                      value={sfondo}
                      onChange={(e) => setSfondo(e.target.value)}
                      onBlur={() => setSfondo(value => normalizeImageUrl(value))}
                      placeholder="https://..."
                      aria-invalid={Boolean(sfondo && getImageUrlError(sfondo))}
                      disabled={isDuplicateMode}
                      required
                    />
                    {sfondo && getImageUrlError(sfondo) && (
                      <div className="invalid-feedback d-block">
                        {getImageUrlError(sfondo)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">URL Logo*</label>
                    <input
                      type="text"
                      inputMode="url"
                      className={`form-control ${logo && getImageUrlError(logo) ? 'is-invalid' : ''}`}
                      value={logo}
                      onChange={(e) => setLogo(e.target.value)}
                      onBlur={() => setLogo(value => normalizeImageUrl(value))}
                      placeholder="https://..."
                      aria-invalid={Boolean(logo && getImageUrlError(logo))}
                      disabled={isDuplicateMode}
                      required
                    />
                    {logo && getImageUrlError(logo) && (
                      <div className="invalid-feedback d-block">
                        {getImageUrlError(logo)}
                      </div>
                    )}
                  </div>

                  {/* Preview Immagini */}
                  {(sfondo || logo) && (
                    <div className="col-12">
                      <div className="row g-2">
                        <ImagePreview
                          field="sfondo"
                          label="Sfondo"
                          src={sfondo}
                          variant="background"
                          onStatusChange={handleImagePreviewStatus}
                        />
                        <ImagePreview
                          field="logo"
                          label="Logo"
                          src={logo}
                          variant="logo"
                          onStatusChange={handleImagePreviewStatus}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Pulsanti creazione rapida */}
                {!isDuplicateMode && (
                <div className="admin-panel-soft mb-4">
                  <label className="form-label fw-bold mt-2">⚡ Creazione rapida</label>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => quickCreateShow('Raw', 1, 23, 59)} // lunedì ore 23:59
                    >
                      Raw
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => quickCreateShow('SmackDown', 5, 23, 59)} // venerdì ore 23:59
                    >
                      SmackDown
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-light btn-sm"
                      onClick={() => quickCreateShow('NXT', 2, 23, 59)} // martedì ore 23:59
                    >
                      NXT
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                      onClick={() => quickCreateShow('AAA', 6, 23, 59)} // sabato ore 23:59
                    >
                      AAA
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => quickCreateShow('TNA iMPACT', 4, 23, 59)} // giovedì ore 23:59
                    >
                      TNA
                    </button>
                  </div>
                  <small className="text-muted">
                    Imposta automaticamente nome, tipo settimanale, prossima data e ora 23:59. Puoi modificarli prima di salvare.
                  </small>
                </div>
                )}

                <div className="d-flex flex-column flex-sm-row gap-2 mt-4">
                  <button type="submit" className="btn btn-primary flex-grow-1" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Salvataggio...</>
                    ) : (
                      <><i className={`bi ${editingId ? 'bi-save' : isDuplicateMode ? 'bi-copy' : 'bi-plus-lg'}`}></i> {editingId ? 'Aggiorna' : isDuplicateMode ? 'Duplica' : 'Crea'}</>
                    )}
                  </button>
                  {(editingId || isDuplicateMode) && (
                    <button type="button" className="btn btn-outline-secondary flex-grow-1" onClick={resetForm}>
                      <i className="bi bi-x-lg"></i> Annulla
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Lista Shows a blocchi (card) */}
        <div className="card shadow-sm admin-list-card">
          <div className="card-header bg-light py-3">
            <h3 className="h6 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span><i className="bi bi-calendar2-week me-2"></i>Tutti gli show</span>
              <span className="badge bg-secondary">{shows.length} show</span>
            </h3>
          </div>

          <div className="card-body p-3">
            {shows.length === 0 ? (
              <div className="alert alert-info mb-0">
                <i className="bi bi-info-circle me-2"></i>Nessuno show disponibile. Crea il primo!
              </div>
            ) : (
              <>
                {yesterdayShows.length > 0 && (
                  <div className="mb-4">
                    <h4 className="h6 text-primary border-bottom pb-2 mb-3">
                      <i className="bi bi-lightning-charge me-2"></i>Show di Ieri ({yesterdayShows.length})
                    </h4>
                    <div className="row g-3">
                      {yesterdayShows.map((show) => renderShowCard(show, { cardIdSuffix: `yesterday-${show.id}` }))}
                    </div>
                  </div>
                )}

                {/* Sezione Show Futuri */}
                {futureShows.length > 0 && (
                  <div className="mb-4">
                    <h4 className="h6 text-success border-bottom pb-2 mb-3">
                      <i className="bi bi-calendar-check me-2"></i>Prossimi Show ({futureShows.length})
                    </h4>
                    <div className="row g-3">
                      {futureShows.map((show) => renderShowCard(show))}
                    </div>
                  </div>
                )}

                {/* Sezione Show Passati */}
                {olderPastShows.length > 0 && (
                  <div>
                    <h4 className="h6 text-secondary border-bottom pb-2 mb-3">
                      <i className="bi bi-clock-history me-2"></i>Show Passati ({olderPastShows.length})
                    </h4>
                    <div className="row g-3">
                      {paginatedPastShows.map((show) => renderShowCard(show, { cardClassName: 'opacity-75', cardIdSuffix: `past-${show.id}` }))}
                    </div>
                    {totalPastPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => setPastPage((page) => Math.max(1, page - 1))}
                          disabled={pastPage === 1}
                        >
                          <i className="bi bi-chevron-left me-1"></i>
                          Più recenti
                        </button>
                        <span className="small text-muted">
                          Pagina {pastPage} di {totalPastPages}
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => setPastPage((page) => Math.min(totalPastPages, page + 1))}
                          disabled={pastPage === totalPastPages}
                        >
                          Meno recenti
                          <i className="bi bi-chevron-right ms-1"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}

export default AddShow;
//https://www.wwe.com/f/styles/og_image/public/2026/03/WM42_PunkRoman_SUN_16x9_date.jpg punk v reings
//https://www.wwe.com/f/styles/large/public/2026/03/RandyCody.jpg cody v orton
